import { motion } from 'framer-motion';

/**
 * Stacked-card draft-being-written skeleton with line-by-line draw animation.
 *
 * SOURCE: frontend-prod/features/draft/DraftCreationLoader.tsx:191-266
 * Scaled DOWN for the 360px task pane:
 *   - card width 120px (web uses 176-208px)
 *   - body lines 4 instead of 5 (still feels like content arriving)
 *   - logo badge size proportionally smaller
 *
 * Cycle:
 *   t=0           card empty
 *   ~0.55s        title bar draws in (scaleX 0→1)
 *   ~0.7-2.7s     4 body lines draw in sequentially
 *   ~4.3s         everything erases (scaleX → 0)
 *   ~4.8s        cycle restarts
 */
const BODY_LINE_WIDTHS = ['92%', '78%', '88%', '60%'];
const CYCLE_SECONDS = 4.8;
const TITLE_DRAW = 0.55;
const BODY_DRAW = 0.4;
const BODY_GAP = 0.1;
const ERASE_TIME = 0.5;

export function DraftSkeletonCard(): JSX.Element {
  const startEraseT = (CYCLE_SECONDS - ERASE_TIME) / CYCLE_SECONDS;

  return (
    <div className="relative w-[120px]" aria-hidden>
      {/* Two layered shadow copies for depth */}
      <span className="absolute inset-0 rounded-lg bg-white border border-border translate-x-1.5 translate-y-1.5 opacity-40" />
      <span className="absolute inset-0 rounded-lg bg-white border border-border translate-x-1 translate-y-1 opacity-70" />

      {/* The animated draft card */}
      <motion.div
        className="relative rounded-lg bg-white border border-border shadow-sm p-2.5 flex flex-col gap-1.5 overflow-hidden"
        style={{ aspectRatio: '3 / 4' }}
        animate={{ y: [0, -3, 0] }}
        transition={{ repeat: Infinity, duration: 3, ease: 'easeInOut' }}
      >
        {/* Dog-ear corner */}
        <span
          aria-hidden
          className="absolute top-0 right-0 w-3 h-3 bg-muted border-l border-b border-border"
          style={{ clipPath: 'polygon(100% 0, 0 0, 100% 100%)' }}
        />

        {/* Title bar */}
        <motion.div
          className="h-2 rounded-sm bg-foreground/80 mb-0.5"
          style={{ width: '62%', transformOrigin: 'left' }}
          initial={{ scaleX: 0 }}
          animate={{ scaleX: [0, 0, 1, 1, 0] }}
          transition={{
            duration: CYCLE_SECONDS,
            times: [0, 0, TITLE_DRAW / CYCLE_SECONDS, startEraseT, 1],
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />

        {/* Body lines drawn sequentially */}
        {BODY_LINE_WIDTHS.map((width, i) => {
          const startDraw = TITLE_DRAW + 0.15 + i * (BODY_DRAW + BODY_GAP);
          const finishDraw = startDraw + BODY_DRAW;
          const t1 = startDraw / CYCLE_SECONDS;
          const t2 = finishDraw / CYCLE_SECONDS;
          return (
            <motion.div
              key={i}
              className="h-1.5 rounded-sm bg-foreground/30"
              style={{ width, transformOrigin: 'left' }}
              initial={{ scaleX: 0 }}
              animate={{ scaleX: [0, 0, 1, 1, 0] }}
              transition={{
                duration: CYCLE_SECONDS,
                times: [0, t1, t2, startEraseT, 1],
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            />
          );
        })}
      </motion.div>

      {/* Floating logo badge */}
      <motion.div
        className="absolute -top-2 -right-2 w-7 h-7 bg-white border border-primary/30 rounded-full shadow-sm flex items-center justify-center overflow-hidden"
        animate={{ scale: [1, 1.1, 1], y: [0, -1, 0] }}
        transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
      >
        <img
          src="/logo_icon.png"
          alt="NyayAssist"
          width={16}
          height={16}
          style={{ width: 16, height: 16, objectFit: 'contain' }}
        />
      </motion.div>
    </div>
  );
}
